import { z } from 'zod';
import { REQUEST_KINDS, REQUEST_STATUSES } from './requests.js';

/** مخططات طلبات الموظّفين. */

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ بصيغة سنة-شهر-يوم.');

const timeOnly = z
  .string()
  .regex(/^\d{2}:\d{2}$/, 'الوقت بصيغة ساعة:دقيقة.');

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const nullableDate = dateOnly
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

const nullableTime = timeOnly
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

/* ==========================================================================
   أنواع الطلبات وقواعدها
   ========================================================================== */

export const UpsertRequestTypeSchema = z.object({
  label: z.string().trim().min(2, 'اسم النوع قصير.').max(120),
  kind: z.enum(REQUEST_KINDS),
  description: optionalText(500),

  needsDates: z.boolean().default(false),
  needsHours: z.boolean().default(false),
  needsAmount: z.boolean().default(false),
  needsReason: z.boolean().default(true),
  needsAttachment: z.boolean().default(false),

  maxPerMonth: z.coerce.number().int().min(1).max(99).nullable().optional(),
  maxHours: z.coerce.number().min(0.25).max(24).nullable().optional(),
  minNoticeDays: z.coerce.number().int().min(0).max(90).nullable().optional(),

  approverRole: z.enum(['MANAGER', 'ADMIN']).default('MANAGER'),
  isActive: z.boolean().default(true),
  order: z.coerce.number().int().min(0).max(999).default(0),
});
export type UpsertRequestTypeInput = z.infer<typeof UpsertRequestTypeSchema>;

/* ==========================================================================
   الطلب نفسه
   ========================================================================== */

/**
 * ما يملؤه صاحبُ الطلب.
 *
 * كلُّ الحقول اختياريّةٌ هنا: ما يُطلب فعلًا تحدّده قواعدُ النوع، وتُفحص
 * في `checkRequest` — والمخطّطُ لا يعرف أيَّ نوعٍ أُرسل.
 */
export const CreateRequestSchema = z.object({
  typeId: Cuid,
  onDate: nullableDate,
  toDate: nullableDate,
  fromTime: nullableTime,
  toTime: nullableTime,
  hours: z.coerce.number().min(0).max(24).nullable().optional(),
  amount: z.coerce.number().min(0).max(9_999_999).nullable().optional(),
  reason: optionalText(2000),
  attachmentId: Cuid.nullable().optional(),
});
export type CreateRequestInput = z.infer<typeof CreateRequestSchema>;

/**
 * البتُّ في الطلب.
 *
 * الرفضُ يطلب سببًا: من رُفض طلبُه بلا سببٍ يسأل مديرَه في الممرّ، وذاك
 * هو ما بُني هذا المحرّك ليمنعه.
 */
export const DecideRequestSchema = z
  .object({
    approve: z.boolean(),
    note: optionalText(1000),
  })
  .refine((v) => v.approve || !!v.note, {
    message: 'الرفض يحتاج سببًا مكتوبًا.',
    path: ['note'],
  });
export type DecideRequestInput = z.infer<typeof DecideRequestSchema>;

export const RequestsQuerySchema = z.object({
  status: z.enum(REQUEST_STATUSES).optional(),
  typeId: Cuid.optional(),
  employeeId: Cuid.optional(),
  from: dateOnly.optional(),
  to: dateOnly.optional(),
});
export type RequestsQuery = z.infer<typeof RequestsQuerySchema>;
